import {
  UNIT_DEATH_CAST,
  UNIT_STARVATION_CAST,
  UNIT_STARVATION_FIELD_OPEN_PAUSE_MS,
  UNIT_STARVATION_POLL_MS,
} from '../configs/unitDeathAnimation';
import {resolveUnitRecruitmentTier} from '../configs/unitRecruitmentAnimation';
import {GAME_TYPES} from '../constants/game';
import {PHASES} from '../constants/phases';
import {
  selectIsCityCaptureAnimationPlaying,
  selectIsRuinsIncomeAnimationAwaited,
} from '../redux/reducers/privateStateReducer/selectors';
import {
  selectGameResults,
  selectGameType,
  selectPhase,
  selectPlayers,
  selectRound,
} from '../redux/reducers/sharedStateReducer/selectors';
import {
  captureStarvedUnitsHold,
  checkIfAbilityResultHeldByKey,
  holdAbilityResult,
  releaseAbilityResult,
} from '../utils/effects/abilityResultHolds';
import {buildUnitStarvationCastPlan} from '../utils/effects/buildUnitStarvationCast';
import {getActingPlayerFromState} from '../utils/players/getActingPlayer';
import {getPlayer0xColor} from '../utils/players/getPlayer0xColor';
import {checkIfReplayRewinding} from '../utils/replay/replayPlaybackSpeed';
import {checkIfAnimationAllowed} from './playAbilityCastEffects';
import {checkIfUnitInView} from './playUnitDeathEffects';

// Все, что закрывает поле: модалки партии, подсказка рейтинга, итоги партии и оверлей «Ваш ход»
const FIELD_COVER_SELECTOR = [
  '.modal-window_overlay',
  '.ranked-phase-hint-overlay',
  '.match-winner-overlay',
  '.turn-overlay',
].join(',');

const checkIfFieldCovered = (state) => Boolean(
  document.querySelector(FIELD_COVER_SELECTOR)
  || selectIsCityCaptureAnimationPlaying(state)
  || selectIsRuinsIncomeAnimationAwaited(state)
);

let lastBatchNumber = 0;
let pendingBatches = [];
let pollTimerId = null;

// Смотреть гибель уже поздно: фаза строительства кончилась, идет другой раунд или конец партии
const checkIfBatchExpired = (state, batch) => selectRound(state) !== batch.round
  || Boolean(selectGameResults(state)?.length)
  || ![PHASES.SUPPLY, PHASES.CONSTRUCTION].includes(selectPhase(state));

// Свой отряд слышно и за краем кадра: модалки о нем нет, другого знака, что он погиб, тоже
const checkIfOwnUnit = (state, unit) => {
  const players = selectPlayers(state) || [];
  if (selectGameType(state) === GAME_TYPES.LOCAL) {
    return players.some((player) => player.id === unit.ownerId && !player.isBot);
  }
  return getActingPlayerFromState(state)?.id === unit.ownerId;
};

const playStarvationDeaths = (store, hexGridRenderer, soundManager, units) => {
  const {staggerMs, maxStaggerMs, agonyDelayMs} = UNIT_STARVATION_CAST;
  const {viewMarginFactor, fullEffectsLimit} = UNIT_DEATH_CAST;
  const state = store.getState();
  const players = selectPlayers(state) || [];
  const deaths = units
    .map((unit) => ({unit, anchor: hexGridRenderer.getAbilityCastAnchor(unit.hexKey)}))
    .filter(({anchor}) => anchor && checkIfUnitInView(hexGridRenderer.scene, anchor, viewMarginFactor));

  deaths.forEach(({unit, anchor}, index) => {
    const owner = players.find((player) => player.id === unit.ownerId);
    hexGridRenderer.abilityCastAnimation.play(buildUnitStarvationCastPlan({
      unit: anchor,
      hexSize: anchor.hexSize,
      hexRotation: anchor.hexRotation,
      color: owner ? getPlayer0xColor(owner.color) : 0xffffff,
      art: hexGridRenderer.getUnitRecruitmentArt?.(unit, {players, state}),
      tier: resolveUnitRecruitmentTier(unit.type),
      delayMs: Math.min(index * staggerMs, maxStaggerMs),
      isReduced: deaths.length > fullEffectsLimit,
    }));
  });

  if (deaths.length > 0) {
    // Вступает, когда колос начал тлеть: последний всхлип приходится на гибель
    setTimeout(() => soundManager?.playUnitStarvationAgony?.(), agonyDelayMs);
  } else if (units.some((unit) => checkIfOwnUnit(state, unit))) {
    soundManager?.playUnitStarvationAgony?.();
  }
};

const finishBatch = (hexGridRenderer, batch) => {
  if (releaseAbilityResult(batch.holdKey)) {
    hexGridRenderer?.requestFieldRedraw?.();
  }
};

const processBatch = (store, hexGridRenderer, soundManager, batch) => {
  // Сцену разобрали: удержания сняты, партии больше нет
  if (!checkIfAbilityResultHeldByKey(batch.holdKey)) {
    return true;
  }
  const state = store.getState();
  if (!checkIfAnimationAllowed(store, hexGridRenderer) || checkIfBatchExpired(state, batch)) {
    finishBatch(hexGridRenderer, batch);
    return true;
  }
  if (checkIfFieldCovered(state)) {
    batch.fieldOpenAt = null;
    return false;
  }
  // Поле только что открылось: игрок должен успеть заметить, какие отряды остались без снабжения
  batch.fieldOpenAt = batch.fieldOpenAt ?? Date.now();
  if (Date.now() - batch.fieldOpenAt < UNIT_STARVATION_FIELD_OPEN_PAUSE_MS) {
    return false;
  }
  try {
    playStarvationDeaths(store, hexGridRenderer, soundManager, batch.units);
  } catch (error) {
    // Анимация — украшение: отряды должны уйти с поля в любом случае
    console.warn('[UnitStarvation] не удалось показать гибель от голода', error);
  }
  finishBatch(hexGridRenderer, batch);
  return true;
};

const scheduleNextCheck = (store, getHexGridRenderer, getSoundManager) => {
  if (pollTimerId !== null || pendingBatches.length === 0) {
    return;
  }
  pollTimerId = setTimeout(() => {
    pollTimerId = null;
    const hexGridRenderer = getHexGridRenderer();
    const soundManager = getSoundManager();
    pendingBatches = pendingBatches.filter((batch) => !processBatch(store, hexGridRenderer, soundManager, batch));
    scheduleNextCheck(store, getHexGridRenderer, getSoundManager);
  }, UNIT_STARVATION_POLL_MS);
};

/**
 * Отряды, погибшие от голода, остаются на поле, пока поле не откроется: пока на нем модалка,
 * оверлей «Ваш ход», облет городов или рост дохода руин, смотреть гибель некому. Открылось —
 * после короткой паузы играет гибель (см. buildUnitStarvationCastPlan) и агония. Так и не
 * открылось до конца фазы строительства — отряды просто уходят с поля.
 *
 * Настоящего отряда в сторе уже нет: на поле он только рисуется (см. captureStarvedUnitsHold),
 * поэтому правила, клики и сеть живут по настоящему состоянию.
 *
 * @param {Object} store
 * @param {Object} params
 * @param {Object[]} params.units — отряды в том виде, какими они стояли на поле
 * @param {function(): Object} params.getHexGridRenderer — сцену может пересоздать полное состояние
 * @param {function(): Object} params.getSoundManager
 * @returns {boolean} ждут ли отряды показа
 */
export const queueUnitStarvationDeaths = (store, {units, getHexGridRenderer, getSoundManager}) => {
  if (units.length === 0 || checkIfReplayRewinding() || !checkIfAnimationAllowed(store, getHexGridRenderer())) {
    return false;
  }
  lastBatchNumber += 1;
  const batch = {
    holdKey: `starvation:${lastBatchNumber}`,
    units,
    round: selectRound(store.getState()),
    fieldOpenAt: null,
  };
  holdAbilityResult(batch.holdKey, captureStarvedUnitsHold(units));
  pendingBatches.push(batch);
  scheduleNextCheck(store, getHexGridRenderer, getSoundManager);
  return true;
};
